// ─────────────────────────────────────────────────────────────
// ZCall — préférences de matériel (caméra / microchoisi).
// Le choix fait dans le test caméra est mémorisé sur l'appareil et
// réutilisé quand on rejoint une réunion, pour que tout le monde
// n'utilise pas systématiquement la webcam par défaut du système.
// ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'zcall_device_prefs';

export interface ZCallDevicePrefs {
  cameraId: string;
  micId: string;
}

const EMPTY_PREFS: ZCallDevicePrefs = { cameraId: '', micId: '' };

export function readDevicePrefs(): ZCallDevicePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_PREFS };
    const parsed = JSON.parse(raw) as Partial<ZCallDevicePrefs>;
    return {
      cameraId: typeof parsed.cameraId === 'string' ? parsed.cameraId : '',
      micId: typeof parsed.micId === 'string' ? parsed.micId : '',
    };
  } catch {
    return { ...EMPTY_PREFS };
  }
}

export function saveDevicePrefs(patch: Partial<ZCallDevicePrefs>): ZCallDevicePrefs {
  const next = { ...readDevicePrefs(), ...patch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible (navigation privée) */
  }
  return next;
}

interface ZCallVideoConstraints {
  width: { ideal: number };
  height: { ideal: number };
  deviceId?: { exact: string };
}

interface ZCallAudioConstraints {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  deviceId?: { exact: string };
}

export interface ZCallMediaConstraints {
  video: ZCallVideoConstraints;
  audio: ZCallAudioConstraints;
}

/** Contraintes média à partir du matériel choisi. */
export function buildMediaConstraints(prefs: ZCallDevicePrefs = readDevicePrefs()): ZCallMediaConstraints {
  const video: ZCallVideoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };
  const audio: ZCallAudioConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
  };
  if (prefs.cameraId) video.deviceId = { exact: prefs.cameraId };
  if (prefs.micId) audio.deviceId = { exact: prefs.micId };
  return { video, audio };
}

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/** Liste les caméras et micros disponibles (les libellés ne sont complets
 *  qu'après avoir obtenu une première autorisation d'accès). */
export async function listAvDevices(): Promise<{ cameras: DeviceOption[]; mics: DeviceOption[] }> {
  if (!navigator.mediaDevices?.enumerateDevices) return { cameras: [], mics: [] };
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const map = (kind: 'videoinput' | 'audioinput', fallback: string): DeviceOption[] =>
      devices
        .filter((d) => d.kind === kind)
        .map((d, index) => ({
          deviceId: d.deviceId,
          label: d.label || `${fallback} ${index + 1}`,
        }));
    return { cameras: map('videoinput', 'Caméra'), mics: map('audioinput', 'Micro') };
  } catch {
    return { cameras: [], mics: [] };
  }
}